import { memo, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { Box, Button, Container, Header, SpaceBetween } from '@amzn/awsui-components-core';
import { ContentNode, Troubleshooting } from '@amzn/awscat-pattern-library-service-model';
import { getText, TiptapOutput, TiptapOutputType } from '@amzn/awscat-editor';

import { RichtextDocId } from '../../model/RichtextDocId';
import { ActionableWarningButton } from '../v2-common/ActionableWarningButton';
import ConfirmationModalView from '../v2-common/ConfirmationModalView';
import { SortableList, SortableRenderArgs } from '../v2-common/SortableList';
import { scrollIntoComfortZone } from '../v2-common/tiptap-editor-integration/Window';
import { useFilteredInlineComments } from '../v2-hooks/useFilteredInlineComments';
import { useFilteredInlineEdits } from '../v2-hooks/useFilteredInlineEdits';
import { PatternCellField } from './PatternCellField';
import { PatternCellActions } from './PatternCellActions';
import { PatternSuggestionPopover } from './PatternSuggestionPopover';
import { usePatternTroubleshootingEditorViewModel } from './PatternTroubleshootingEditorViewModel';
import styles from './PatternTroubleshootingEditor.module.scss';

interface PatternTroubleshootingEditorProps {
  readonly node: ContentNode;
  readonly contentId: string;
  /** Author guidance rendered beneath the section title. */
  readonly description?: string;
  /** Cloudscape "info" link node rendered inline next to the section title. */
  readonly info?: ReactNode;
}

interface PatternTroubleshootingCellProps {
  readonly rowId: string;
  readonly field: 'issue' | 'solution';
  readonly initialValue: TiptapOutputType;
  readonly isEditing: boolean;
  readonly contentId: string;
  readonly onStageEdit: (
    rowId: string,
    field: 'issue' | 'solution',
    value: TiptapOutputType,
  ) => void;
  readonly onStartEditing: (rowId: string) => void;
  readonly isAutoFocused?: boolean;
}

/** Converts a viewport-relative DOMRect to page-absolute coordinates. */
function toPageRect(rect: DOMRect): DOMRect {
  return new DOMRect(rect.x + window.scrollX, rect.y + window.scrollY, rect.width, rect.height);
}

const PatternTroubleshootingCell = memo(function PatternTroubleshootingCell({
  rowId,
  field,
  initialValue,
  isEditing,
  contentId,
  onStageEdit,
  onStartEditing,
  isAutoFocused = false,
}: PatternTroubleshootingCellProps) {
  const warningButtonRef = useRef<HTMLButtonElement>(null);
  const [popoverTarget, setPopoverTarget] = useState<DOMRect | null>(null);

  // Track edit-mode transitions during render so we can react without a syncing effect.
  const [wasEditing, setWasEditing] = useState(isEditing);

  // When the cell transitions into edit mode, then close any open suggestion popover.
  if (isEditing && !wasEditing) setPopoverTarget(null);
  if (isEditing !== wasEditing) setWasEditing(isEditing);

  // When the row or field changes, then rebuild the doc id used to scope this cell's edits and comments.
  const inlineTextFilter = useMemo<RichtextDocId>(
    () =>
      field === 'issue'
        ? { type: 'troubleshooting_issue', troubleshootingId: rowId }
        : { type: 'troubleshooting_solution', troubleshootingId: rowId },
    [field, rowId],
  );
  const inlineEdits = useFilteredInlineEdits(inlineTextFilter);
  const inlineComments = useFilteredInlineComments(inlineTextFilter);
  const hasSuggestions = inlineEdits.some((edit) => !edit.resolved) || inlineComments.length > 0;

  // When the editor emits a change, then stage it against this cell's row and field.
  const stageEdit = useCallback(
    (output: TiptapOutput) => onStageEdit(rowId, field, output.json),
    [onStageEdit, rowId, field],
  );

  // When this cell requests editing, then enter edit mode on its row.
  const startEditing = useCallback(() => onStartEditing(rowId), [onStartEditing, rowId]);

  const showSuggestionSummary = () => {
    const rect = warningButtonRef.current?.getBoundingClientRect();
    if (rect) {
      const pageRect = toPageRect(rect);
      scrollIntoComfortZone(pageRect);
      setPopoverTarget(pageRect);
    }
  };

  const dismissPopover = useCallback(() => setPopoverTarget(null), []);
  const editFromPopover = useCallback(() => {
    setPopoverTarget(null);
    startEditing();
  }, [startEditing]);

  return (
    <div className={hasSuggestions && !isEditing ? styles.cellWithWarning : undefined}>
      {hasSuggestions && !isEditing && (
        <ActionableWarningButton
          ref={warningButtonRef}
          ariaLabel={`AI suggestions available for ${field}`}
          onClick={showSuggestionSummary}
        />
      )}
      <PatternCellField
        initialValue={initialValue}
        isReadonly={!isEditing}
        onChange={stageEdit}
        isAutoFocused={isAutoFocused}
        contentId={contentId}
        inlineTextFilter={inlineTextFilter}
      />
      {popoverTarget && (
        <PatternSuggestionPopover
          target={popoverTarget}
          onClose={dismissPopover}
          onEdit={editFromPopover}
        />
      )}
    </div>
  );
});

/**
 * Editor for the Troubleshooting section of a Pattern. Renders a
 * three-column table (Issue / Solution / Actions) with inline editing,
 * drag-to-reorder, and delete-with-confirmation.
 *
 * Uses a plain HTML table instead of CloudScape Table because
 * CloudScape Table does not support custom row reordering via
 * drag-and-drop.
 */
export const PatternTroubleshootingEditor = memo(function PatternTroubleshootingEditor({
  node,
  contentId,
  description,
  info,
}: PatternTroubleshootingEditorProps) {
  const vm = usePatternTroubleshootingEditorViewModel();

  // Delete modal is pure view state — ViewModel only exposes the action.
  const [deleteRowId, setDeleteRowId] = useState<string | undefined>(undefined);
  const [deleteIssueText, setDeleteIssueText] = useState('');

  const openDeleteModal = useCallback(
    (rowId: string) => {
      const row = vm?.troubleshootingRows.find((r) => r.id === rowId);
      const issueLabel = row?.issue;
      setDeleteIssueText(
        typeof issueLabel === 'string' || typeof issueLabel === 'object'
          ? // @ts-expect-error TS2345 - strict migration
            getText(issueLabel)
          : '<empty>',
      );
      setDeleteRowId(rowId);
    },
    [vm?.troubleshootingRows],
  );

  const confirmDelete = useCallback(() => {
    if (deleteRowId) vm?.deleteTroubleshootingRow(deleteRowId);
    setDeleteRowId(undefined);
  }, [deleteRowId, vm]);

  const dismissDelete = useCallback(() => {
    setDeleteRowId(undefined);
  }, []);

  const keyExtractor = useCallback((row: Troubleshooting) => row.id, []);

  const renderRow = useCallback(
    (
      row: Troubleshooting,
      { setNodeRef, style, dragHandleProps, isDragging }: SortableRenderArgs,
    ) => {
      const isEditing = vm?.editingRowId === row.id;
      const rowClassName = isDragging
        ? styles.draggingRow
        : isEditing
          ? styles.editingRow
          : undefined;

      return (
        <tr ref={setNodeRef} style={style} className={rowClassName}>
          <td className={styles.cell}>
            <PatternTroubleshootingCell
              rowId={row.id}
              field="issue"
              // @ts-expect-error TS2322 - strict migration
              initialValue={row.issue}
              isEditing={isEditing}
              onStageEdit={(rowId, field, value) => vm?.stageFieldEdit(rowId, field, value)}
              onStartEditing={(rowId) => vm?.startEditing(rowId)}
              isAutoFocused={isEditing}
              contentId={contentId}
            />
          </td>
          <td className={styles.cell}>
            <PatternTroubleshootingCell
              rowId={row.id}
              field="solution"
              // @ts-expect-error TS2322 - strict migration
              initialValue={row.solution}
              isEditing={isEditing}
              onStageEdit={(rowId, field, value) => vm?.stageFieldEdit(rowId, field, value)}
              onStartEditing={(rowId) => vm?.startEditing(rowId)}
              contentId={contentId}
            />
          </td>
          <td className={styles.actionsCell}>
            <PatternCellActions
              isEditing={isEditing}
              onEditClick={() => vm?.startEditing(row.id)}
              onSaveClick={() => vm?.saveEditing()}
              onCancelClick={() => vm?.cancelEditing()}
              onDeleteClick={() => openDeleteModal(row.id)}
              dragHandleProps={dragHandleProps}
              isDragging={isDragging}
            />
          </td>
        </tr>
      );
    },
    [vm, openDeleteModal, contentId],
  );

  if (!vm) return null;

  return (
    <Container
      header={
        <Header
          variant="h3"
          info={info}
          description={
            description && (
              <Box variant="span" color="text-body-secondary" fontSize="body-s">
                {description}
              </Box>
            )
          }
        >
          {node.title}
        </Header>
      }
      disableContentPaddings
    >
      <SpaceBetween size="m">
        {vm.troubleshootingRows.length > 0 ? (
          [
            <SortableList
              key="rows"
              items={vm.troubleshootingRows as Troubleshooting[]}
              keyExtractor={keyExtractor}
              onReorder={vm.reorderTroubleshootingRows}
              item={renderRow}
              container={(children: ReactNode) => (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.headerCell}>Issue</th>
                      <th className={styles.headerCell}>Solution</th>
                      <th className={styles.headerCellActions}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>{children}</tbody>
                </table>
              )}
            />,
            <div key="add-row" className={styles.buttonArea}>
              <Button iconName="add-plus" onClick={vm.addTroubleshootingRow}>
                Add issue
              </Button>
            </div>,
          ]
        ) : (
          <Box textAlign="center" padding={{ horizontal: 'l', bottom: 'l' }}>
            <Button iconName="add-plus" onClick={vm.addTroubleshootingRow}>
              Add issue
            </Button>
          </Box>
        )}
      </SpaceBetween>

      {deleteRowId && (
        <ConfirmationModalView
          header="Delete troubleshooting entry?"
          body={`The entry "${deleteIssueText || 'Untitled'}" will be removed. This cannot be undone.`}
          confirmLabel="Delete"
          isConfirming={false}
          onConfirm={confirmDelete}
          onDismiss={dismissDelete}
        />
      )}
    </Container>
  );
});
